<template>
    <modal-wrapper
        class="w-xs"
        data-testid="sms-phone-selector-modal"
        :header="t('smsCommunication.selectPhone')"
        @close="emit('close')"
    >
        <div class="px-4 py-2">
            <div
                class="mb-4 rounded-md border border-orange-200 bg-orange-50 p-3"
                data-testid="sms-phone-selector-historical-warning"
                v-if="historicalPhone"
            >
                <p class="mb-2 text-sm text-orange-900">
                    {{ t('smsCommunication.historicalPhoneMismatch') }}
                </p>
                <div
                    class="cursor-not-allowed rounded border border-gray-200 bg-gray-100 px-3 py-2 font-mono text-gray-400"
                    aria-disabled="true"
                    data-testid="sms-phone-selector-historical-phone"
                >
                    {{ formatPhone(historicalPhone) || historicalPhone }}
                </div>
            </div>

            <p class="text-sm text-gray-600 mb-4" data-testid="sms-phone-selector-instruction">
                {{
                    historicalPhone
                        ? t('smsCommunication.selectCurrentPhone', { lead: leadName })
                        : `${leadName} ${t('smsCommunication.hasMultiplePhones')}`
                }}
            </p>

            <ul class="space-y-2 mb-6 max-h-60 overflow-y-auto">
                <li
                    class="border rounded-md px-4 py-2 cursor-pointer flex items-center justify-between transition-colors"
                    :class="{
                        'bg-blue-50 border-blue-400 hover:bg-blue-100':
                            selectedPhone?.id === phone.id,
                        'hover:bg-blue-50': selectedPhone?.id !== phone.id,
                    }"
                    data-testid="sms-phone-selector-option"
                    :key="phone.id"
                    @click="selectPhone(phone)"
                    v-for="phone in phoneOptions"
                >
                    <span
                        class="text-gray-500 text-sm"
                        data-testid="sms-phone-selector-type"
                        v-if="phone.type"
                    >
                        {{ formatPhoneType(phone.type) }}:
                    </span>

                    <span class="grow text-center">
                        <span
                            class="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700"
                            data-testid="sms-phone-selector-preferred"
                            v-if="phone.preferred"
                        >
                            {{ t('general.preferred') }}
                        </span>
                    </span>

                    <span class="font-mono" data-testid="sms-phone-selector-phone-number">
                        {{ formatPhone(phone.phone) }}
                    </span>
                </li>
            </ul>
        </div>

        <template #actions>
            <div class="flex justify-end gap-x-3">
                <activix-button data-testid="cancel-button" @click="emit('close')">
                    {{ t('modal.cancel') }}
                </activix-button>

                <activix-button
                    :color="ButtonColor.Blue"
                    :disabled="!hasPhoneSelected"
                    data-testid="apply-button"
                    @click="apply"
                >
                    {{ t('modal.confirm') }}
                </activix-button>
            </div>
        </template>
    </modal-wrapper>
</template>

<script setup lang="ts">
    import { ref, computed } from 'vue';
    import { useI18n } from '@/hooks/useI18n.ts';
    import { usePhoneType } from '@/hooks/usePhoneType.ts';
    import { formatPhone } from '@/utils/index.js';
    import ActivixButton from '@/components/activix-ui/ActivixButton/ActivixButton.vue';
    import { ButtonColor } from '@/components/activix-ui/ActivixButton/types.ts';
    import ModalWrapper from '@/components/DynamicModalContentWrapper.vue';
    import ConversationLeadPhone from '@/entities/ConversationLeadPhone.ts';

    interface Props {
        leadName: string;
        onSelect: (phone: ConversationLeadPhone) => void | Promise<void>;
        phoneOptions: ConversationLeadPhone[];
        historicalPhone?: string | null;
    }

    interface Emits {
        (e: 'close'): void;
    }

    const props = defineProps<Props>();

    const emit = defineEmits<Emits>();

    const { t } = useI18n();
    const { formatPhoneType } = usePhoneType();

    const selectedPhone = ref<ConversationLeadPhone | null>(
        props.historicalPhone ? props.phoneOptions.find((phone) => phone.preferred) ?? null : null
    );

    const hasPhoneSelected = computed<boolean>(() => {
        return !!selectedPhone.value;
    });

    const selectPhone = (phone: ConversationLeadPhone) => {
        selectedPhone.value = phone;
    };

    const apply = async () => {
        if (!selectedPhone.value) {
            return;
        }

        const confirmedPhone = selectedPhone.value;
        await props.onSelect(confirmedPhone);
        emit('close');
    };
</script>
